import { ApiResponse } from '../types';

// Dynamic API base URL that works with custom domains
const API_BASE_URL = (() => {
  // In development, always use localhost
  if (process.env.NODE_ENV !== 'production') {
    return 'http://localhost:5001/api';
  }

  // In production, check if we're on a custom domain
  const currentDomain = window.location.hostname;
  const mainDomains = [
    'localhost',
    'charity-auctions-web-app.onrender.com',
    'auctions.decoded.digital',
  ];

  // If we're on a custom domain, use the current domain for API calls
  if (!mainDomains.includes(currentDomain)) {
    return `${window.location.origin}/api`;
  }

  // If we're on the main domain, use the configured API base URL or current origin
  return process.env.REACT_APP_API_BASE_URL
    ? `${process.env.REACT_APP_API_BASE_URL}/api`
    : `${window.location.origin}/api`;
})();

/** What the platform charges, as set by a system administrator. */
export interface PlatformFeeSettings {
  ownStripeFeePercentage: number;
  platformStripeFeePercentage: number;
  perItemFeeUsdCents: number;
  suggested: {
    ownStripeFeePercentage: number;
    platformStripeFeePercentage: number;
    perItemFeeUsdCents: number;
  };
  configured: boolean;
  updatedAt: string | null;
}

/** What one auction owed the platform when it closed. */
export interface PlatformFeeStatement {
  id: string;
  auctionId: string;
  accountId: string;
  auctionTitle: string;
  model: 'OWN_STRIPE' | 'PLATFORM_STRIPE';
  feeRate: number;
  perItemFeeUsdCents: number;
  itemsListed: number;
  itemsSold: number;
  auctionCurrency: string;
  grossSoldCents: number;
  fxRateToUsd: number;
  fxRateFetchedAt: string;
  fxRateSource: string;
  grossSoldUsdCents: number;
  percentageFeeUsdCents: number;
  perItemTotalUsdCents: number;
  totalUsdCents: number;
  billingCardOnFile?: boolean;
  status: 'UNPAID' | 'PAID' | 'WAIVED';
  createdAt: string;
  settledAt?: string;
  notes?: string;
}

export interface PlatformAdminAccount {
  id: string;
  name: string;
  email: string;
  role: string;
  customDomain?: string;
  domainVerified?: boolean;
  /** The platform's cut of each payment this account takes, as a percentage. 0 for everyone today. */
  platformFeePercentage: number;
  stripeConnectAccountId: string | null;
  useOwnStripeAccount: boolean;
  userCount: number;
  auctionCount: number;
  createdAt: Date;
}

export interface PlatformAdminUser {
  id: string;
  email: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  role: string;
  accountId: string;
  accountName: string;
  address?: string;
  phone?: string;
  profileImageUrl?: string;
  isActive: boolean;
  blockedAt?: Date | null;
  invitedAt?: Date;
  activatedAt?: Date;
  lastLoginAt?: Date;
  lastRealLoginAt?: Date;
  createdAt: Date;
}

export interface PlatformAdminAuction {
  id: string;
  title: string;
  description?: string;
  status: string;
  type: string;
  visibility: string;
  accountId: string;
  accountName: string;
  startTime?: Date;
  endTime?: Date;
  bidCount: number;
  createdAt: Date;
}

export interface PlatformAdminTransaction {
  id: string;
  bidId: string;
  auctionId: string;
  itemId?: string;
  auctionTitle: string;
  accountId: string;
  accountName: string;
  userId: string;
  userEmail: string;
  userName: string;
  /** Donations: the donor asked not to be named in the public donation list. */
  isAnonymous?: boolean;
  amount: number;
  currency: string;
  type: string;
  // Item information
  item?: {
    id: string;
    title: string;
  };
  paymentMethod?: {
    id: string;
    card: {
      brand: string;
      last4: string;
      expMonth: number;
      expYear: number;
    };
  };
  transaction: {
    status: string;
    paymentIntentId: string;
    createdAt: Date;
  };
  createdAt: Date;
  // Auctioneer information
  auctioneerName: string;
  auctioneerEmail: string;
}

export interface PlatformStats {
  totalAccounts: number;
  totalUsers: number;
  totalAuctions: number;
  totalBids: number;
  totalTransactions: number;
  activeAuctions: number;
  recentAccounts: number;
  recentAuctions: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface PlatformAdminUsersResponse {
  users: PlatformAdminUser[];
  /** How many users hold each role across the whole platform, not just this page. */
  roleCounts?: Record<string, number>;
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

class PlatformAdminService {
  private async request<T>(endpoint: string, options?: RequestInit): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...options?.headers,
        },
        ...options,
      });

      if (!response.ok) {
        // Handle HTTP errors
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = await response.json();
          if (errorData.error?.message) {
            errorMessage = errorData.error.message;
          }
        } catch {
          // If we can't parse the error response, use the default message
        }

        return {
          success: false,
          error: {
            message: errorMessage,
            code: response.status.toString()
          }
        } as ApiResponse<T>;
      }

      return response.json();
    } catch (error) {
      // Handle network errors (server not running, connection issues, etc.)
      // Platform Admin API request failed

      let errorMessage = 'Network error. Please check your connection and try again.';
      if (error instanceof Error) {
        if (error.message.includes('Failed to fetch')) {
          errorMessage = 'Unable to connect to the server. Please ensure the server is running and try again.';
        } else {
          errorMessage = error.message;
        }
      }

      return {
        success: false,
        error: {
          message: errorMessage,
          code: 'NETWORK_ERROR'
        }
      } as ApiResponse<T>;
    }
  }

  async getStats(): Promise<PlatformStats> {
    const response = await this.request<PlatformStats>('/platform-admin/stats');
    if (!response.success) {
      throw new Error(response.error.message);
    }
    return response.data;
  }

  async getAccounts(page = 1, limit = 50): Promise<PaginatedResponse<PlatformAdminAccount>> {
    const response = await this.request<PaginatedResponse<PlatformAdminAccount>>(`/platform-admin/accounts?page=${page}&limit=${limit}`);
    if (!response.success) {
      throw new Error(response.error.message);
    }
    return response.data;
  }

  /**
   * A page of users, filtered by the server.
   *
   * Role and search go to the server rather than being applied to whatever
   * page happened to load: with a few hundred bidders ahead of them, the
   * handful of auctioneers and admins sit past the first page entirely.
   */
  async getUsers(page = 1, limit = 50, role?: string, search?: string): Promise<PlatformAdminUsersResponse> {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (role && role !== 'all') params.set('role', role);
    if (search?.trim()) params.set('search', search.trim());

    const response = await this.request<PlatformAdminUsersResponse>(`/platform-admin/users?${params}`);
    if (!response.success) {
      throw new Error(response.error.message);
    }
    return response.data;
  }

  async getAuctions(page = 1, limit = 50): Promise<PaginatedResponse<PlatformAdminAuction>> {
    const response = await this.request<PaginatedResponse<PlatformAdminAuction>>(`/platform-admin/auctions?page=${page}&limit=${limit}`);
    if (!response.success) {
      throw new Error(response.error.message);
    }
    return response.data;
  }

  async getTransactions(page = 1, limit = 50): Promise<PaginatedResponse<PlatformAdminTransaction>> {
    const response = await this.request<PaginatedResponse<PlatformAdminTransaction>>(`/platform-admin/transactions?page=${page}&limit=${limit}`);
    if (!response.success) {
      throw new Error(response.error.message);
    }
    return response.data;
  }

  async sendEmail(userIds: string[], subject: string, message: string): Promise<{
    totalSent: number;
    totalFailed: number;
    successful: Array<{userId: string; email: string; success: boolean}>;
    failed: Array<{userId?: string; email?: string; success: boolean; error: string}>;
  }> {
    const response = await this.request<{
      totalSent: number;
      totalFailed: number;
      successful: Array<{userId: string; email: string; success: boolean}>;
      failed: Array<{userId?: string; email?: string; success: boolean; error: string}>;
    }>('/platform-admin/send-email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userIds,
        subject,
        message
      }),
    });

    if (!response.success) {
      throw new Error(response.error.message);
    }

    return response.data;
  }

  /** Block someone from the whole platform, or let them back in. */
  async setUserBlocked(userId: string, blocked: boolean): Promise<{ id: string; blockedAt: Date | null }> {
    const response = await this.request<{ id: string; blockedAt: Date | null }>(
      `/platform-admin/users/${userId}/${blocked ? 'block' : 'unblock'}`,
      { method: 'POST' }
    );
    if (!response.success) {
      throw new Error(response.error.message);
    }
    return response.data;
  }

  async updateUserRole(userId: string, role: string): Promise<any> {
    const response = await this.request(`/platform-admin/users/${userId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    });
    return response;
  }

  /**
   * Set the platform's cut for one account.
   *
   * Platform administrators only, and deliberately not part of the account
   * settings an auctioneer can edit - it is a commercial term, not a preference.
   */
  async updatePlatformFee(accountId: string, platformFeePercentage: number): Promise<any> {
    const response = await this.request(`/platform-admin/accounts/${accountId}/platform-fee`, {
      method: 'PUT',
      body: JSON.stringify({ platformFeePercentage }),
    });
    return response;
  }

  /** What the platform charges today. Zero everywhere means nothing is billed. */
  async getPlatformFeeSettings(): Promise<{ success: boolean; data: PlatformFeeSettings; error?: any }> {
    return this.request('/platform-admin/settings') as any;
  }

  /** Sets what the platform charges, for auctions closing from now on. */
  async updatePlatformFeeSettings(settings: {
    ownStripeFeePercentage: number;
    platformStripeFeePercentage: number;
    perItemFeeUsdCents: number;
  }): Promise<{ success: boolean; data: PlatformFeeSettings; error?: any }> {
    return this.request('/platform-admin/settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    }) as any;
  }

  /**
   * The statements of what auctioneers owe the platform, newest first.
   *
   * Totals come from the server, which sees every statement; the page only ever
   * holds the rows it is showing and could not add them up correctly.
   */
  async getPlatformFees(status?: 'UNPAID' | 'PAID' | 'WAIVED'): Promise<{
    success: boolean;
    data: {
      statements: PlatformFeeStatement[];
      totals: { totalUsdCents: number; unpaidUsdCents: number; paidUsdCents: number };
      count: number;
    };
  }> {
    const query = status ? `?status=${status}` : '';
    return this.request(`/platform-admin/platform-fees${query}`) as any;
  }

  /** Records a statement as settled, or written off. The amounts never change. */
  async settlePlatformFee(
    statementId: string,
    status: 'PAID' | 'WAIVED' | 'UNPAID',
    notes?: string
  ): Promise<any> {
    return this.request(`/platform-admin/platform-fees/${statementId}/settle`, {
      method: 'POST',
      body: JSON.stringify({ status, notes }),
    });
  }

  async moveAuction(auctionId: string, targetAccountId: string): Promise<any> {
    const response = await this.request(`/platform-admin/auctions/${auctionId}/move`, {
      method: 'POST',
      body: JSON.stringify({ targetAccountId }),
    });
    return response;
  }

  async updateTransaction(transactionId: string, updates: {
    amount?: number;
    description?: string;
    status?: string;
    type?: string;
    notes?: string;
  }): Promise<any> {
    const response = await this.request(`/platform-admin/transactions/${transactionId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    return response;
  }

  async deleteFailedTransaction(transactionId: string): Promise<any> {
    const response = await this.request(`/platform-admin/transactions/${transactionId}`, {
      method: 'DELETE',
    });
    return response;
  }
}

export const platformAdminService = new PlatformAdminService();
